import {
  ColDef,
  ColumnResizedEvent,
  ColumnState,
  GridApi,
} from 'ag-grid-community'

export function getOrderedColumnsFromState<
  T extends { field?: string; colId?: string },
>(
  savedState: string | null,
  baseColumns: T[],
  gridApiRef?: GridApi | null
): ColDef[] | undefined {
  if (!savedState) return undefined
  if (!gridApiRef) return undefined
  const state = JSON.parse(savedState)
  const orderedColumns: ColDef[] = []
  const usedKeys = new Set<string>()

  // Match by field, falling back to colId so columns without a `field`
  // (e.g. an actions/edit-button column) are preserved when restoring state.
  const getColKey = (col: T): string | undefined => col.field ?? col.colId

  state.forEach((colState: ColumnState) => {
    const col = baseColumns.find((c) => getColKey(c) === colState.colId)
    if (col) {
      const colCopy: T & { hide?: boolean; width?: number } = { ...col }
      if ('width' in colCopy && colState.width !== undefined) {
        colCopy.width = colState.width
      }
      // Restore persisted visibility so a later columnDefs rebuild (e.g. date
      // change) does not revert to the base `hide` value the user overrode.
      if (colState.hide !== undefined && colState.hide !== null) {
        colCopy.hide = colState.hide
      }
      orderedColumns.push(colCopy)
      const key = getColKey(col)
      if (key) usedKeys.add(key)
    }
  })

  baseColumns.forEach((col) => {
    const key = getColKey(col)
    if (key && !usedKeys.has(key)) {
      orderedColumns.push(col)
    }
  })

  return orderedColumns
}

/**
 * Determines whether persisted column state is still compatible with the
 * current column definitions, without relying on a separate version tag.
 *
 * The saved state is considered incompatible (i.e. produced by an older UI
 * layout) when any current column is not represented in it. This happens when
 * new columns are added: an old state has no entry for them, so restoring it
 * would omit those columns / apply an outdated layout. In that case the caller
 * should discard the state and rebuild from current defaults.
 *
 * Returns true when there is no saved state (nothing to migrate) or the state
 * is unparsable (handled elsewhere), and true when every current column key is
 * present in the saved state.
 */
export function isColumnStateCompatible<
  T extends { field?: string; colId?: string },
>(savedState: string | null, currentColumns: T[]): boolean {
  if (!savedState) return true

  let state: ColumnState[]
  try {
    state = JSON.parse(savedState) as ColumnState[]
  } catch {
    // Unparsable state is treated as compatible here; the restore path handles
    // the parse failure by falling back to defaults.
    return true
  }

  if (!Array.isArray(state)) return true

  const savedColIds = new Set(
    state.map((colState) => colState.colId).filter(Boolean)
  )

  const getColKey = (col: T): string | undefined => col.field ?? col.colId

  // Incompatible if any current column is absent from the saved state.
  return currentColumns.every((col) => {
    const key = getColKey(col)
    return key ? savedColIds.has(key) : true
  })
}

const PROGRAMMATIC_COLUMN_EVENT_SOURCES = new Set<string>([
  'api',
  'gridInitializing',
  'gridOptionsChanged',
  'rowModelUpdated',
  'rowDataUpdated',
])

export function isProgrammaticColumnEvent(source?: string): boolean {
  return !!source && PROGRAMMATIC_COLUMN_EVENT_SOURCES.has(source)
}

// Column-resize event sources treated as user-initiated (should persist).
// 'uiColumnResized' = manual drag; 'autosizeColumns' = the column menu's
// "Autosize This Column" and "Autosize All Columns" actions.
const USER_RESIZE_SOURCES = new Set<ColumnResizedEvent['source']>([
  'uiColumnResized',
  'autosizeColumns',
])

/**
 * Whether a `columnResized` event should persist the new column widths.
 * True only for finished, user-initiated resizes — manual drag and the column
 * menu's autosize actions — so programmatic resizes don't overwrite state.
 */
export function isPersistableColumnResize(event: ColumnResizedEvent): boolean {
  return event.finished === true && USER_RESIZE_SOURCES.has(event.source)
}
